import { defineStore } from 'pinia'
import axios from 'axios'

import { ResourceResponse, uuid } from '@/types/general'
import { OngoingReport } from '@/types/dashboard'

export type FetchParametersKey = string

export interface FetchParameters {
    dashboardId: uuid
    shareToken: string | null
}

interface OngoingReportStoreState {
    reports: Record<FetchParametersKey, OngoingReport[]>
    fetching: Record<FetchParametersKey, boolean>
    highlightedReportId: uuid | null
    selectedReportId: uuid | null
}

/**
 * Ongoing reports store.
 * Because it's possible to specify different parameters for each widget we can't just keep a single list of ongoing reports
 * Therefore each list of ongoing reports will be keyed by their unique parameters.
 * By doing this we can still share the result between widgets that use the same parameters.
 *
 * We don't necessarily need to worry about cleanup since parameters only change when dashboard is edited
 */
export const useOngoingReportStore = defineStore('OngoingReportStore', {
    state: (): OngoingReportStoreState => ({
        reports: {},
        fetching: {},
        highlightedReportId: null,
        selectedReportId: null,
    }),

    actions: {
        getFetchParametersKey(parameters: FetchParameters) {
            return parameters.dashboardId
        },

        setHighlightedReport(report: OngoingReport) {
            this.highlightedReportId = report.id
        },

        resetHighlightedReport() {
            this.highlightedReportId = null
        },

        setSelectedReport(report: OngoingReport) {
            this.selectedReportId = report.id
        },

        resetSelectedReport() {
            this.selectedReportId = null
        },

        async fetch(parameters: FetchParameters) {
            const fetchParametersKey = this.getFetchParametersKey(parameters)

            // Avoid fetching if it's already in progress
            if (this.fetching[fetchParametersKey]) {
                return
            }

            this.fetching[fetchParametersKey] = true

            try {
                const response = await axios.get<ResourceResponse<OngoingReport[]>>(
                    window.route('dashboards.dr-ongoing-reports', {
                        dashboard: parameters.dashboardId,
                        shareToken: parameters.shareToken,
                    }),
                )
                this.reports[fetchParametersKey] = response.data.data
            } finally {
                this.fetching[fetchParametersKey] = false
            }
        },
    },
})
